/* Feb-2026 · Cancel-subscription confirmation with winback offer.
 *
 * Two-stage:
 *   1. Confirm dialog — "Are you sure? Here's what you lose."
 *   2. If the winback endpoint returns a coupon, we show a stay-offer
 *      screen with the code. If not, we redirect the user to the
 *      Stripe Billing Portal to complete the cancel.
 */
import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Sparkles, Copy, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

async function post(path, body = {}) {
  const r = await fetch(`${API}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`Request failed (${r.status})`);
  return r.json();
}

export default function CancelSubscriptionDialog({ open, onOpenChange }) {
  const [step, setStep]     = useState('confirm');  // confirm | stay | portal
  const [busy, setBusy]     = useState(false);
  const [offer, setOffer]   = useState(null);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await post('/subscriptions/cancel-request');
      if (r?.winback) {
        setOffer(r.winback);
        setStep('stay');
      } else {
        setStep('portal');
      }
    } catch {
      toast.error('Could not record cancel request. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  const goPortal = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/subscriptions/portal`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ return_url: window.location.origin }),
      });
      const d = await r.json();
      if (d?.url) window.location.href = d.url;
      else toast.error(d?.detail || 'Billing portal unavailable.');
    } catch {
      toast.error('Billing portal unavailable.');
    } finally {
      setBusy(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(offer.coupon);
      toast.success('Coupon copied. It also lives in your inbox.');
    } catch {
      toast.error('Copy failed — grab it from the field above.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="cancel-subscription-dialog">
        {step === 'confirm' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[14px]">
                <AlertTriangle className="h-4 w-4 text-amber-500" /> Cancel your subscription?
              </DialogTitle>
              <DialogDescription className="text-[11.5px]">
                Your manuscripts stay yours — we'll never delete them. But you'll lose access to:
              </DialogDescription>
            </DialogHeader>
            <ul className="space-y-1 text-[12px] text-muted-foreground">
              <li>• AI drafting and editorial rewrites</li>
              <li>• KDP / Adobe / Canva export tools</li>
              <li>• The bookstore publishing pipeline</li>
              <li>• Any unspent word credits and bonus tools</li>
            </ul>
            <DialogFooter className="pt-2">
              <Button variant="outline" size="sm" onClick={() => onOpenChange?.(false)} data-testid="cancel-back-btn">
                Keep my plan
              </Button>
              <Button size="sm" variant="destructive" disabled={busy} onClick={submit} data-testid="cancel-continue-btn">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Continue to cancel
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'stay' && offer && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[14px]">
                <Sparkles className="h-4 w-4 text-emerald-500" /> Stay for {offer.discount_pct}% off × {offer.months} months?
              </DialogTitle>
              <DialogDescription className="text-[11.5px]">
                Before you go — here's a one-time thank-you. Coupon is already on your account and applies at your next renewal.
              </DialogDescription>
            </DialogHeader>
            <div className="rounded-md border border-emerald-500/40 bg-emerald-500/5 p-3">
              <div className="text-[10.5px] uppercase tracking-wider text-emerald-700">Your winback coupon</div>
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 rounded-sm bg-background px-2 py-1 font-mono-stat text-[14px]" data-testid="winback-coupon-code">
                  {offer.coupon}
                </code>
                <Button size="sm" variant="outline" onClick={copyCode} data-testid="winback-copy-btn">
                  <Copy className="h-3.5 w-3.5" /> Copy
                </Button>
              </div>
              <div className="mt-2 text-[10.5px] text-muted-foreground">
                Expires {new Date(offer.expires_at).toLocaleDateString()}.{' '}
                {offer.email_sent ? 'A copy is in your inbox.' : ''}
              </div>
            </div>
            <DialogFooter className="pt-2">
              <Button variant="outline" size="sm" onClick={goPortal} disabled={busy} data-testid="winback-still-cancel-btn">
                No thanks, still cancel
              </Button>
              <Button size="sm" onClick={() => onOpenChange?.(false)} data-testid="winback-stay-btn">
                I'll stay — apply the coupon
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'portal' && (
          <>
            <DialogHeader>
              <DialogTitle className="text-[14px]">Complete cancel in the billing portal</DialogTitle>
              <DialogDescription className="text-[11.5px]">
                We've recorded your cancel intent. Finish in the Stripe billing portal — you'll get a confirmation email once it's processed.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="pt-2">
              <Button variant="outline" size="sm" onClick={() => onOpenChange?.(false)}>Close</Button>
              <Button size="sm" onClick={goPortal} disabled={busy} data-testid="cancel-portal-btn">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Open billing portal
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
